// src/App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Home from "./components/Home";
import Note from "./components/Note";
import "./App.css";
import { Link as ScrollLink } from "react-scroll";
import { SpotifyButton } from "./components/CustomButton";
import { ClipLoader } from "react-spinners";
import Callback from "./components/Callback";
import { useInView } from "react-intersection-observer";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_FLASK_API;
  console.log("API URL:", apiUrl);

  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref3, inView3] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref4, inView4] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    // Check if user is authenticated on component mount
    fetch(`${apiUrl}/`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      mode: "cors",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.authenticated) {
          setIsAuthenticated(true);
        } else if (data.auth_url) {
          setIsAuthenticated(false);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error checking authentication:", error);
        setError("Failed to check authentication. Please try again later.");
        setLoading(false);
        setIsAuthenticated(false);
      });
  }, []);

  const handleLogin = () => {
    fetch(`${apiUrl}/`, {
      credentials: "include", // Important: include credentials for cookies
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.auth_url) {
          window.location.href = data.auth_url; // Redirect to Spotify login
        } else {
          setError("Failed to get authorization URL. Please try again later.");
        }
      })
      .catch((error) => {
        console.error("Error fetching auth URL:", error);
        setError("Failed to initiate login. Please try again later.");
      });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-white bg-gradient-to-b from-gray-900 to-gray-800">
        <ClipLoader
          color="#c084fc"
          size={50}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen text-white bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/home" />
              ) : (
                <main>
                  {/* Hero Section */}
                  <section className="h-screen flex flex-col justify-center items-center text-center p-4">
                    <div className="space-y-8 max-w-3xl px-4">
                      <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-300 to-purple-600 text-transparent bg-clip-text block pb-2">
                        Write. Listen. Remember.
                      </h1>
                      <p className="text-xl text-gray-300">
                        Ever read an old note and wished you could remember how
                        it felt when you wrote it? With NoteSong, every note is
                        saved alongside the music you were listening to at that
                        moment.
                      </p>
                      <div className="space-y-4">
                        <SpotifyButton onClick={handleLogin}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="120"
                            height="60"
                            className="h-6"
                            fill="white"
                          >
                            {/* Your Spotify SVG content here */}
                          </svg>
                        </SpotifyButton>
                      </div>
                      <div className="mt-8">
                        <ScrollLink
                          to="how-it-works"
                          smooth={true}
                          duration={500}
                          className="cursor-pointer text-gray-400 hover:text-purple-400 transition-colors"
                        >
                          Learn more ↓
                        </ScrollLink>
                      </div>
                    </div>
                  </section>

                  {/* How It Works Section */}
                  <section
                    ref={ref1}
                    id="how-it-works"
                    className={`min-h-screen bg-gray-800/50 backdrop-blur-lg py-20 transition-opacity duration-1000 ${
                      inView1 ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <div className="max-w-4xl mx-auto px-4">
                      <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-spotify-green text-transparent bg-clip-text">
                        How It Works
                      </h2>
                      <div className="space-y-8">
                        {[1, 2, 3].map((step) => (
                          <div
                            key={step}
                            className="p-6 bg-gray-800/50 rounded-xl border border-gray-700 shadow-xl"
                          >
                            <div className="flex items-start space-x-6">
                              <div className="text-3xl">{step}</div>
                              <div>
                                <h3 className="text-xl font-semibold mb-2">
                                  {step === 1 && "Connect Your Spotify"}
                                  {step === 2 && "Start Writing"}
                                  {step === 3 && "Relive Your Memories"}
                                </h3>
                                <p className="text-gray-300">
                                  {step === 1 &&
                                    "Securely link your Spotify account to start tracking your listening history."}
                                  {step === 2 &&
                                    "Create notes while listening to music. NoteSong automatically links your notes to the current track."}
                                  {step === 3 &&
                                    "Revisit your notes and play the associated music to experience your memories in full."}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* Features Section */}
                  <section
                    ref={ref2}
                    className={`min-h-screen bg-gray-800/50 backdrop-blur-lg py-20 transition-opacity duration-1000 ${
                      inView2 ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <div className="max-w-4xl mx-auto px-4">
                      <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-spotify-green text-transparent bg-clip-text">
                        Why NoteSong?
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[
                          {
                            emoji: "🎧",
                            title: "Music-Linked Notes",
                            description:
                              "Your thoughts are automatically paired with your songs, so you can relive the exact mood when you revisit them.",
                          },
                          {
                            emoji: "📝",
                            title: "Simple & Seamless",
                            description:
                              "Just start writing. NoteSong takes care of tracking your music in real time.",
                          },
                          {
                            emoji: "💾",
                            title: "Saved, Synced, Yours",
                            description:
                              "Your notes and music history stay with you, making every memory richer.",
                          },
                          {
                            emoji: "🔁",
                            title: "Replay the Feeling",
                            description:
                              "Music brings back memories. NoteSong brings back everything.",
                          },
                        ].map((feature, index) => (
                          <div
                            key={index}
                            className="p-6 bg-gray-800/50 rounded-xl border border-gray-700 shadow-xl"
                          >
                            <div className="text-3xl mb-4">{feature.emoji}</div>
                            <h3 className="text-xl font-semibold mb-2">
                              {feature.title}
                            </h3>
                            <p className="text-gray-300">
                              {feature.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* Testimonials Section */}
                  <section
                    ref={ref3}
                    className={`min-h-screen bg-gray-800/50 backdrop-blur-lg py-20 transition-opacity duration-1000 ${
                      inView3 ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <div className="max-w-4xl mx-auto px-4">
                      <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-spotify-green text-transparent bg-clip-text">
                        What Users Are Saying
                      </h2>
                      <div className="max-w-2xl mx-auto">
                        <div className="p-6 bg-gray-800/50 rounded-xl border border-gray-700 shadow-xl">
                          <p className="text-gray-300 italic">
                            "Wow, it has so many use cases!"
                          </p>
                          <p className="mt-4 text-gray-400">
                            - Jonah, Business Analyst
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                </main>
              )
            }
          />
          <Route
            path="/callback"
            element={<Callback setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route
            path="/home"
            element={isAuthenticated ? <Home /> : <Navigate to="/" />}
          />
          <Route
            path="/notes/:noteId"
            element={<Note />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
